import { useRef, useState } from 'react';
import styled from 'styled-components';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { getViewModel$ } from './viewModel$/viewModel$';
import { ComponentMatrixViewProperties } from './types';
import { ComponentMatrixViewContainer } from './view/atoms';
import { componentMatrix as ComponentMatrix } from './view/componentMatrix';
import { getExportsForHtmlView } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { Team } from '@ardoq/profiling';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { ComponentMatrixSettingsBar } from './ComponentMatrixSettingsBar';
import { ComponentMatrixViewLegend } from './ComponentMatrixViewLegend';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { noop } from 'lodash';
import { isPresentationMode } from 'env';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.COMPONENT_MATRIX;

const ContentContainer = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  &.forExport {
    overflow: visible;
    width: fit-content;
    flex: unset;
  }
`;

const ComponentMatrixView = ({
  viewModel,
  viewSettings,
  viewSettingsState,
  hasReferences,
  hasFilteredNullItems,
  errors,
  i18nLocale,
  errorInfoBoxLabels,
  userLocale: locale,
}: ComponentMatrixViewProperties) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const getContainer = () => containerRef.current;

  const [clearedErrors, setClearedErrors] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_COMPONENT_MATRIX
  );

  const noVisibleComponents = !viewModel.items.length;
  const noReferencesOrListFields = !(
    hasReferences || viewSettingsState.listFields.length
  );
  const isEmptyView = noVisibleComponents || noReferencesOrListFields;

  return (
    <ComponentMatrixViewContainer
      className={`tab-pane ${VIEW_ID}Tab active react-view`}
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
    >
      <ComponentMatrixSettingsBar
        viewSettingsState={viewSettingsState}
        viewSettings={viewSettings}
        locale={locale}
        i18nLocale={i18nLocale}
        exports={{
          ...getExportsForHtmlView({
            container: getContainer,
            exportedViewMetadata: {
              name: VIEW_ID,
              isViewpointMode: false,
            },
            ...getSharedExportFunctions(),
            labels: getExportLabels(i18nLocale),
          }),
          isDisabled: isEmptyView,
        }}
      />
      {isEmptyView ? (
        <EmptyStateBase
          noVisibleComponents={noVisibleComponents}
          noReferencesOrListFields={noReferencesOrListFields}
          hasFilteredNullItems={hasFilteredNullItems}
          allDescendantsIncluded={viewSettings.includeAllDescendants}
        />
      ) : (
        <ContentContainer ref={containerRef}>
          <ComponentMatrix viewModel={viewModel} />
          <ComponentMatrixViewLegend
            legendComponentTypes={viewModel.legendComponentTypes}
            isLegendActive={viewSettings.isLegendActive}
            i18nLocale={i18nLocale}
          />
          <ErrorInfoBox
            errors={clearedErrors ? [] : errors}
            hasClones={false}
            clearHasClones={noop}
            isShowNeverAgainSet={neverShowAgain}
            isPresentationMode={isPresentationMode()}
            clearErrors={() => setClearedErrors(true)}
            toggleNeverShowAgain={toggleNeverShowAgain}
            {...errorInfoBoxLabels}
          />
        </ContentContainer>
      )}
    </ComponentMatrixViewContainer>
  );
};
const PerformanceTrackedComponentMatrix = (
  props: ComponentMatrixViewProperties
) => {
  usePerformanceTracking('component matrix view render', 1000, {
    team: Team.INSIGHT,
    viewId: VIEW_ID,
    metadata: { itemCount: props.viewModel.items.length },
  });

  return <ComponentMatrixView {...props} />;
};

const ConnectedComponentMatrix = connectInstanceWithProps(
  PerformanceTrackedComponentMatrix,
  getViewModel$
);

export { ConnectedComponentMatrix };
